/**
 * Setting your location.
 *
 * Two ways in, in this order:
 *
 *   1. The browser tells us, if the trader allows it.
 *   2. They drop a pin on a map.
 *
 * The second is not a degraded fallback — a denied permission is a perfectly
 * ordinary answer, and plenty of people would rather point at a street corner than
 * hand over their device's idea of where they are. So the map is always reachable,
 * not something you have to be refused into.
 *
 * There is deliberately no address box. This product answers one question — how far
 * away is this card — and a place name cannot answer it. A name would also be a
 * second version of the location that can disagree with the coordinate, and personal
 * data we would then have to justify holding.
 */
import React from "react";
import { api } from "./api.jsx";
import { useApp } from "./store.jsx";
import { hostingShop, hoursSummary } from "./hours.jsx";

const { Button, Card, Icon, Input } = window.CardboardmarketDesignSystem_2a3239;

/** Roughly the centre of Spain: where the map opens when we know nothing yet. */
const FALLBACK_CENTRE = [40.4168, -3.7038];

/** The tiles both maps here draw on. */
const OSM_TILES = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

function PinMap({ value, onChange }) {
  const host = React.useRef(null);
  const map = React.useRef(null);
  const marker = React.useRef(null);

  React.useEffect(() => {
    if (!host.current || map.current) return;
    const start = value ?? FALLBACK_CENTRE;
    map.current = window.L.map(host.current, { attributionControl: false, zoomControl: true })
      .setView(start, value ? 15 : 6);
    window.L.tileLayer(OSM_TILES, { maxZoom: 19 }).addTo(map.current);

    marker.current = window.L.marker(start, { draggable: true }).addTo(map.current);
    marker.current.on('dragend', () => {
      const { lat, lng } = marker.current.getLatLng();
      onChange([lat, lng]);
    });
    // Tapping is how most people will do this; dragging is for the adjustment after.
    map.current.on('click', (e) => {
      marker.current.setLatLng(e.latlng);
      onChange([e.latlng.lat, e.latlng.lng]);
    });
    // Leaflet measures its container on creation, and inside a dialog that has just
    // opened the container is often still zero-height.
    setTimeout(() => map.current && map.current.invalidateSize(), 0);
  }, []);

  // Keep the pin in step when the position changes from outside (geolocation).
  React.useEffect(() => {
    if (!value || !marker.current) return;
    marker.current.setLatLng(value);
    map.current.setView(value, Math.max(map.current.getZoom(), 15));
  }, [value ? value[0] : null, value ? value[1] : null]);

  return (
    <div
      ref={host}
      style={{ height: 220, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-hairline)', overflow: 'hidden' }}
    />
  );
}

/**
 * A PIN TO LOOK AT, not to move: a shop's counter, on its own form. The owner,
 * 2026-10-19: "if we're going to show a 'Where the door is' with a pin, the
 * set pin should be shown." Moving it stays the dialog's job.
 *
 * Exact, because a shop's counter is already public on its own page — this
 * is never used for a person's point.
 *
 * THE BOX IS THERE BEFORE THE PIN IS. The shop arrives after the first render,
 * so the frame is drawn at its final size from the start and the map fills it
 * when the point comes: nothing below it moves.
 *
 * The marker's own position is written back onto the frame as data, so a test
 * can ask where the pin IS rather than where it was asked to be.
 */
export function PinView({ value, height = 160 }) {
  const host = React.useRef(null);
  const map = React.useRef(null);
  const marker = React.useRef(null);

  const mark = () => {
    if (!host.current || !marker.current) return;
    const at = marker.current.getLatLng();
    host.current.dataset.markerLat = String(at.lat);
    host.current.dataset.markerLng = String(at.lng);
  };

  React.useEffect(() => {
    if (!value || !host.current || !window.L) return;
    if (!map.current) {
      map.current = window.L.map(host.current, {
        attributionControl: false, zoomControl: false, dragging: false, scrollWheelZoom: false,
        doubleClickZoom: false, boxZoom: false, keyboard: false, touchZoom: false, tap: false,
      }).setView(value, 16);
      window.L.tileLayer(OSM_TILES, { maxZoom: 19 }).addTo(map.current);
      marker.current = window.L.marker(value, { interactive: false, keyboard: false }).addTo(map.current);
      setTimeout(() => map.current && map.current.invalidateSize(), 0);
    } else {
      marker.current.setLatLng(value);
      map.current.setView(value, map.current.getZoom());
    }
    mark();
  }, [value ? value[0] : null, value ? value[1] : null]);

  React.useEffect(() => () => { if (map.current) { map.current.remove(); map.current = null; } }, []);

  return (
    <div ref={host} data-testid="counter-map" aria-hidden="true"
         style={{ height, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-hairline)',
                  overflow: 'hidden', background: 'var(--surface-sunken)' }} />
  );
}

/**
 * `value` is [lat, lng] or null. `onChange` receives the same.
 * Nothing here ever displays a coordinate: a trader recognises a map, not numbers.
 */
export function LocationPicker({ value, onChange }) {
  const { t } = useApp();
  const [state, setState] = React.useState('idle');   // idle | asking | denied | ready
  const [showMap, setShowMap] = React.useState(Boolean(value));
  // A saved point usually arrives AFTER the first render — /api/me has to answer
  // first — and the initial state above is read once. Without this the map stayed
  // hidden for anybody who already had a location, which is precisely the person
  // asking "where am I set to?".
  React.useEffect(() => { if (value) setShowMap(true); }, [value && value.join(',')]);

  const ask = () => {
    if (!navigator.geolocation) { setState('denied'); setShowMap(true); return; }
    setState('asking');
    navigator.geolocation.getCurrentPosition(
      (pos) => { onChange([pos.coords.latitude, pos.coords.longitude]); setState('ready'); setShowMap(true); },
      // Refusal and failure are the same outcome for us: show the map.
      () => { setState('denied'); setShowMap(true); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <Button variant={value ? 'outline' : 'primary'} onClick={ask} disabled={state === 'asking'}>
          <Icon name="map-pin" size={15} />
          {state === 'asking' ? t("Locating\u2026") : value ? t("Use my location again") : t("Use my location")}
        </Button>
        {showMap ? null : (
          <Button variant="ghost" onClick={() => setShowMap(true)}>{t("Drop a pin instead")}</Button>
        )}
      </div>

      {state === 'denied' ? (
        <p className="cbm-meta" style={{ margin: 0, color: 'var(--text-muted)' }}>
          {t("No location from your browser — drop a pin on the map instead.")}
        </p>
      ) : null}

      {showMap ? (
        <>
          <PinMap value={value} onChange={onChange} />
          <p className="cbm-meta" style={{ margin: 0, color: 'var(--text-muted)' }}>
            {value ? t("Tap or drag the pin to adjust.") : t("Tap the map to place your pin.")}
          </p>
        </>
      ) : null}
    </div>
  );
}


/**
 * WHEN, which a trade between two PEOPLE still needs.
 *
 * It carried a shop's opening hours and refused a time outside them, until the
 * owner replaced that rule with a simpler one: a trade at a shop's counter has
 * no time at all, because the shop's hours are the time. So the field is not
 * rendered for those at all — see the propose dialog — and what is left here is
 * the ordinary one.
 */
export function MeetWhen({ value, onChange }) {
  const { t } = useApp();
  return (
    <Input label={t("When")} type="datetime-local" value={value}
           onChange={(e) => onChange(e.target.value)} />
  );
}

/**
 * Choosing where to meet.
 *
 * Lived only in the accept dialog, so the person PROPOSING a trade got a plain
 * text box while the person accepting got a real place search. Same decision,
 * same two people, two different tools — and the proposer is the one who has
 * never been there, so they needed it more.
 *
 * Deliberately a button rather than a keystroke autocomplete: the geocoder's
 * usage policy is one request a second for the whole application, and a meeting
 * place is chosen once per trade. Searching on demand respects that without a
 * debounce racing a shared budget.
 */
export function PlacePicker({ name, onName, note, onNote, pin, onPin, theirShop, myShop }) {
  const { point, t } = useApp();

  /*
   * A TRADE WITH A SHOP HAPPENS AT ITS COUNTER, so there is nothing to pick.
   *
   * The owner's rule, and the server enforces it — a proposal naming anywhere
   * else is refused. Which makes offering a search here worse than useless: it
   * invites somebody to type a café and then tells them off for it. So when a
   * shop is hosting, this stops being a picker and becomes a statement.
   *
   * Either side's, in the same order the server takes them: the seller's
   * counter first because that is where the card is, then the viewer's own
   * where they are the shop and the other party is a person.
   */
  const host = hostingShop(theirShop, myShop);

  /*
   * Set once, rather than asked for. The place is not in doubt, and a form
   * whose only answer has to be tapped before it is allowed through is
   * ceremony — the server fills a blank one in anyway.
   */
  React.useEffect(() => {
    if (!host) return;
    if ((name || '') === host.name) return;
    onName(host.name);
    if (onNote) onNote(host.address || '');
    if (onPin && host.point) onPin([host.point.lat, host.point.lng]);
  }, [host && host.name]);
  /*
   * GAME SHOPS AS NEUTRAL GROUND.
   *
   * The best thing in the shops feature and the owner's immediate yes. Meeting
   * somebody from the internet to swap a card is the awkward part of this whole
   * product, and a public, staffed, indoor room full of people doing exactly
   * that solves it better than any amount of software could.
   *
   * NEAR WHOEVER IS CHOOSING, and the server decides that — not the midpoint
   * between the two of you, which would tell a stranger roughly where the other
   * person lives. See the note on `/api/shops/nearby`.
   *
   * Their own shop goes FIRST where they have one, because "meet at your shop"
   * is the obvious answer the moment one of you is a shop, and it should not be
   * somewhere down a list ordered by distance from me.
   */
  const [nearby, setNearby] = React.useState([]);
  React.useEffect(() => {
    let live = true;
    api.nearbyShops(point ? { lat: point[0], lng: point[1] } : undefined)
      .then((r) => { if (live) setNearby(r.shops || []); })
      // Silent. Shops are a shortcut, and a shortcut that fails must not put an
      // error on the screen where somebody is arranging to meet a person.
      .catch(() => { if (live) setNearby([]); });
    return () => { live = false; };
  }, [point ? point[0] : null, point ? point[1] : null]);

  const shops = React.useMemo(() => {
    const rest = (nearby || []).filter((s) => !theirShop || s.name !== theirShop.name);
    return (theirShop ? [{ ...theirShop, theirs: true }] : []).concat(rest).slice(0, 6);
  }, [nearby, theirShop && theirShop.name]);

  const pickShop = (shop) => {
    onName(shop.name);
    if (onNote) onNote(shop.address || '');
    if (onPin) onPin([shop.point.lat, shop.point.lng]);
  };
  const [results, setResults] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  /*
   * Whether a search has actually run, held apart from whether it found
   * anything. Without it there is no way to tell "you have not searched yet"
   * from "nothing matched", and the second one rendered as an empty space — the
   * whole reason a working search was reported as broken.
   */
  const [searched, setSearched] = React.useState(false);
  // How far the SERVER looked, as it reported it. Not a number kept here, which
  // would drift from the one actually used.
  const [withinKm, setWithinKm] = React.useState(null);

  const search = async () => {
    const q = (name || '').trim();
    if (q.length < 3) return;
    setSearching(true);
    try {
      const r = await api.searchPlaces({
        q, limit: 6,
        ...(point ? { lat: point[0], lng: point[1] } : {}),
      });
      setResults(r.places);
      setWithinKm(r.withinMetres == null ? null : Math.round(r.withinMetres / 1000));
    } catch (e) {
      setResults([]);
    } finally { setSearching(false); setSearched(true); }
  };

  const choose = (place) => {
    setSearched(false);
    onName(place.name);
    if (onNote) onNote(place.address || '');
    if (onPin) onPin([place.lat, place.lng]);
    setResults([]);
  };

  /*
   * THE WHOLE CONTROL, REPLACED. Not disabled inputs with the answer typed in
   * them: a greyed-out field somebody cannot change reads as something broken,
   * where a sentence naming the shop reads as the fact it is.
   */
  if (host) {
    return (
      <Card tone="sunken" padding="var(--space-5)" style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-start' }}>
        <Icon name="store" size={18} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ font: 'var(--type-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>
            {host.name}
          </div>
          {host.address ? (
            <div style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-body)', marginTop: 2 }}>
              {host.address}
            </div>
          ) : null}
          <div style={{ font: 'var(--type-meta)', color: 'var(--text-muted)', marginTop: 6 }}>
            {t("Trades with a store happen at its counter.")}
          </div>
          {host.hours ? (
            <div style={{ font: 'var(--type-meta)', color: 'var(--text-muted)', marginTop: 2 }}>
              {hoursSummary(host.hours, t)}
            </div>
          ) : null}
        </div>
      </Card>
    );
  }

  return (
    <>
      {/*
        ABOVE THE SEARCH, because it is the answer for most trades and typing a
        café name is the fallback. Nothing renders where there are no verified
        shops nearby, which is most of Spain today and will stay that way until
        this feature works.
      */}
      {shops.length ? (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <span className="cbm-label">{t("Meet at a game store")}</span>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginTop: 'var(--space-2)' }}>
            {shops.map((s) => (
              <Button key={s.name + s.address} size="sm" variant="outline"
                onClick={() => pickShop(s)}
                title={s.address}>
                {s.theirs ? t("{shop} (theirs)", { shop: s.name }) : s.name}
              </Button>
            ))}
          </div>
          <span style={{ display: 'block', font: 'var(--type-meta)', color: 'var(--text-muted)', marginTop: 'var(--space-2)' }}>
            {t("A game store is somewhere public, indoors and open to everyone.")}
          </span>
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end' }}>
        <Input wrapperStyle={{ flex: 1 }} label={t("Meeting point")} icon="map-pin" placeholder={t("Café Central")}
          value={name}
          onChange={(e) => { onName(e.target.value); setSearched(false); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } }} />
        <Button variant="outline" onClick={search} disabled={searching || (name || '').trim().length < 3}>
          {searching ? t("Looking…") : t("Find it")}
        </Button>
      </div>
      {/*
        The range, said out loud.
        The search has always been limited to a box around the trader, and the
        interface has never mentioned it — so a place outside that box produced an
        empty list with no explanation, which is indistinguishable from a fault.
        The number comes from the answer, so it cannot disagree with the search
        that was actually run.
      */}
      {searched && withinKm ? (
        <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {results.length
            ? t("Places within {km} km of you.", { km: withinKm })
            : t("Nothing by that name within {km} km of you. Check the spelling, or just type where you want to meet — the name is yours to write.", { km: withinKm })}
        </span>
      ) : searched && !results.length ? (
        <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {t("Nothing found by that name. You can type where you want to meet instead.")}
        </span>
      ) : null}
      {results.length ? (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-sm)' }}>
          {results.map((r, i) => (
            <li key={`${r.name}-${i}`}>
              <button type="button" onClick={() => choose(r)}
                style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 0, borderTop: i ? '1px solid var(--border-hairline)' : 0, padding: '10px 12px', cursor: 'pointer' }}>
                <span style={{ display: 'block', font: 'var(--type-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>{r.name}</span>
                <span style={{ display: 'block', font: 'var(--type-meta)', color: 'var(--text-muted)' }}>{r.address}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {onNote ? (
        <Input label={t("How to find it")} placeholder={t("Tables outside, quiet before noon")} value={note} onChange={(e) => onNote(e.target.value)} />
      ) : null}
      {onPin ? (
        <div>
          <div className="cbm-label" style={{ marginBottom: 'var(--space-3)' }}>{t("Put it on the map (optional)")}</div>
          <LocationPicker value={pin} onChange={onPin} />
        </div>
      ) : null}
    </>
  );
}

